import * as React from 'react';
import { useEffect, useState, useRef } from "react";
import ReactPaginate from "react-paginate";
import {
  getAdminInitData,
  getAdminReusableObjects, 
} from "../../../../api/sdk.gen";
import { InitData, ReusableObject } from "../../../../api/types.gen";
import Cookies from "js-cookie";
import Select from 'react-select';
import VersionsButton from "../../Versions/Button";
import MySwal from "../../../../ui/MySwal";

import { formatDate } from '../../../../helpers/dates'
import { truncateWithTooltip, getMultiselectOptions } from '../../../../helpers/utils'

import { UnwrappedNewEditDocApp } from "../../../../startup/NewEditDocApp";

type AdminAssessmentReusableObjectsProps = {
    currentUser: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    [key: string]: any;
  };
  editorContext?: {
    isEditorMode: boolean;
    permissions?: any;
    onAddClick?: (reusableObject: ReusableObject) => void;
  };
};

const AdminAssessmentReusableObjects = (props: AdminAssessmentReusableObjectsProps) => {
  const [initData, setInitData] = useState<InitData>({} as InitData);
  const [initDataLoaded, setInitDataLoaded] = useState(false);
  const [reusableObjects, setReusableObjects] = useState<ReusableObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [questionCoursesOptions, setQuestionCoursesOptions] = useState([]);

  const [sortColumn, setSortColumn] = useState<string>(
    Cookies.get("sortColumn") || "id"
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    (Cookies.get("sortDirection") as "asc" | "desc") || "asc"
  );

  const params = new URLSearchParams(location.search);
  const projectId = params.get('project_id');
  const initialFilters: { [key: string]: string | string[] } = {};
  params.forEach((value, key) => {
    if (key.includes("[]")) {
      const cleanKey = key.replace("[]", "");
      if (!initialFilters[cleanKey]) {
        initialFilters[cleanKey] = [];
      }
      (initialFilters[cleanKey] as string[]).push(value);
    } else {
      initialFilters[key] = value;
    }
  });

  const [page, setPage] = useState<number>(parseInt(params.get('page')) || 1);
  const [per, setPer] = useState<number>(parseInt(params.get('per')) || 10);
  const [filters, setFilters] = useState<{ [key: string]: string | string[] }>(initialFilters);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState("auto");

  const [selectedTeachingPoint, setSelectedTeachingPoint] = useState<ReusableObject | undefined>(undefined);

  const closeTeachingPointModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setSelectedTeachingPoint(undefined);
  };

  const openTeachingPointModal = (event: React.MouseEvent<HTMLButtonElement>, reusableObject: ReusableObject) => {
    event.preventDefault();
    setSelectedTeachingPoint(reusableObject);
  };

  useEffect(() => {
    const updateTableHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const footerHeight = footerRef.current?.offsetHeight || 0;
      const viewportHeight = window.innerHeight;

      const maxHeight = Math.max(500, viewportHeight - headerHeight - footerHeight - 100);
      setTableMaxHeight(`${maxHeight}px`);
    };

    // Set height on mount and resize
    setTimeout(() => {
      updateTableHeight();
    }, 1000)

    window.addEventListener("resize", updateTableHeight);

    return () => {
      window.removeEventListener("resize", updateTableHeight);
    };
  }, []);

  useEffect(() => {
    fetchInitData();
  }, [])

  useEffect(() => {
    if (initData && Object.keys(initData).length > 0) {
      setQuestionCoursesOptions(initData.question_courses || []);
      setInitDataLoaded(true)
    }
  }, [initData])

  // Update URL params when filters change
  useEffect(() => {
    const newParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => newParams.append(`${key}[]`, val));
      } else {
        newParams.set(key, value);
      }
    });

    if (page) newParams.set('page', page.toString());
    if (per) newParams.set('per', per.toString());

    // Update the browser's URL without reloading the page
    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    window.history.pushState(null, "", newUrl);
  }, [page, per, filters]);

  const fetchInitData = async () => {
    setLoading(true)
    try {
      const response = await getAdminInitData();
      setInitData(response.data);
    } catch (error) {
      console.error("Error fetching init data:", error);
    } finally {
      setLoading(false)
    }
  }

  const fetchReusableObjects = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        per,
      };

      if (sortColumn && sortDirection) {
        params.sort_col = sortColumn;
        params.sort_dir = sortDirection;
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params[`filters[${key}][]`] = value;
        } else {
          params[`filters[${key}]`] = value;
        }
      });

      const response = await getAdminReusableObjects({
        query: params,
      });

      setReusableObjects(response.data.data);
      setRowCount(response.data.meta?.count || 0);
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReusableObjects();
  }, [page, per, sortColumn, sortDirection, filters]);

  useEffect(() => {
    Cookies.set("sortColumn", sortColumn || "");
    Cookies.set("sortDirection", sortDirection || "");
  }, [sortColumn, sortDirection]);

  const formatLabel = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
  }

  const handleFilterChange = (column: string, value: string) => {
    const updatedFilters = { ...filters };
    if (value && value.trim() !== '') {
      updatedFilters[column] = value;
    } else {
      delete updatedFilters[column];
    }
    setFilters(updatedFilters);
    setPage(1);
  };

  const handleFilterChangeMulti = (column: string, selectedOptions: { label: string; value: string }[]) => {
    const updatedFilters = { ...filters };
    if (selectedOptions && selectedOptions.length > 0) {
      const selectedValues = selectedOptions.map(option => option.value);
      updatedFilters[column] = selectedValues;
    } else {
      delete updatedFilters[column];
    }
    setFilters(updatedFilters);
    setPage(1);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageIds = reusableObjects.map((item) => item.id);
      setSelectedRows(prev => new Set([...prev, ...currentPageIds]));
    } else {
      const currentPageIds = new Set(reusableObjects.map((item) => item.id));
      setSelectedRows(prev => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleExport = async (onlySelected) => {
    const confirmExport = await MySwal.fire({
      title: 'This report can take a while to produce. You can monitor its completion on the Reports page.',
      showCancelButton: true,
      confirmButtonText: 'Yes, export it!',
      cancelButtonText: 'Cancel',
    });

    if (confirmExport.isConfirmed) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/admin/assessment/reusable_objects/export';

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'authenticity_token';
        csrfInput.value = csrfToken;
        form.appendChild(csrfInput);
      }

      if (onlySelected) {
        const idsInput = document.createElement('input');
        idsInput.type = 'hidden';
        idsInput.name = 'ids';
        idsInput.value = Array.from(selectedRows).join(',');
        form.appendChild(idsInput);
      }

      document.body.appendChild(form);
      form.submit();
    }
  };

  const addButtonColumn = props.editorContext?.isEditorMode && props.editorContext?.permissions?.insertRloToDocuments ? [
    {
      key: "add_button",
      name: "ADD",
      sortable: false,
      filterType: "none",
      renderer: (reusableObject: ReusableObject) => (
        <button
          className="button small"
          onClick={() => props.editorContext?.onAddClick?.(reusableObject)}
        >
          Add
        </button>
      )
    }
  ] : [];
  const showExportControls = !props.editorContext?.isEditorMode;

  const columns = [
    ...addButtonColumn,
    ...(showExportControls ? [{
      key: "select",
      name: "",
      sortable: false,
      filterType: "none",
      renderer: (item) => (
        <input
          type="checkbox"
          checked={selectedRows.has(item.id)}
          onChange={(e) => handleSelectRow(item.id, e.target.checked)}
        />
      )
    }] : []),
    {
      key: "id",
      name: "ID",
      sortable: true,
      filterType: "text",
    },
    {
      key: "uid",
      name: "UID",
      sortable: true,
      filterType: "text",
    },
    {
      key: "name",
      name: "Name",
      sortable: true,
      filterType: "text",
      renderer: (reusableObject) => (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            setSelectedTeachingPoint(reusableObject);
          }}
        >
          {reusableObject.content_title || reusableObject.name}
        </a>
      ),
    },
    {
      key: "documents",
      name: "Cases",
      sortable: false,
      filterKey: 'document_id',
      filterType: "multiselect",
      options: questionCoursesOptions,
      renderer: (item, val) => (
        <div>
          {item.documents.map((doc: { id: number; title: string }) => (
            <div key={doc.id}>
              <a
                href={`/documents/${doc.id}${projectId ? `?project_id=${projectId}` : ''}`}
                target='_blank'
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {doc.title}
              </a>
            </div>
          ))}
        </div>
      )
    },
    {
      key: "mapped_learning_objectives",
      name: "Mapped LOs / Cases",
      sortable: false,
      filterType: "none",
      renderer: (item) => {
        const mappedLos = (item as any).mapped_learning_objectives || [];

        if (!mappedLos.length) {
          return <div>-</div>;
        }

        return (
          <div>
            {mappedLos.map((lo) => (
              <div key={lo.id} style={{ marginBottom: '8px' }}>
                <div><strong>{lo.name}</strong></div>
                <div>
                  {lo.shared_cases?.length
                    ? lo.shared_cases.map((sharedCase) => sharedCase.title).join(', ')
                    : 'No shared cases'}
                </div>
              </div>
            ))}
          </div>
        );
      }
    },
    {
      key: "assessment_questions",
      name: "Questions",
      sortable: false,
      filterType: "none",
      renderer: (item, val) => (
        <div>
          <a
            href={`/admin/assessment/questions?learning_objective_id=${item.id}`}
            target='_blank'
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {item.assessment_questions.length} items
          </a>

          {item.assessment_questions.map((question: { id: number; case_summary_identifier: string }) => (
            <div key={question.id}>
              <a
                href={`/admin/assessment/questions?id=${question.id}`}
                target='_blank'
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {question.case_summary_identifier}
              </a>
            </div>
          ))}
        </div>
      )
    },
    {
      key: "description",
      name: "Description",
      sortable: true,
      filterType: "text",
    },
    {
      key: "kind",
      name: "Kind",
      sortable: true,
      filterType: "text",
    },
    {
      key: "listed",
      name: "Listed",
      sortable: true,
      filterType: "text",
    },
    {
      key: "data",
      name: "Data",
      sortable: true,
      filterType: "none",
      renderer: (item, val) => (
        <div>
          {Object.entries(item.data).map(([key, value]) => (
            <div key={key}>
              <strong>{key}:</strong> {String(value)}
            </div>
          ))}
        </div>
      )
    },
    {
      key: "publish_variant_id",
      name: "Publish variant",
      sortable: true,
      filterType: "none",
    },
    {
      key: "review_variant_id",
      name: "Review variant",
      sortable: true,
      filterType: "none",
    },
    {
      key: "maintenance_variant_id",
      name: "Maintenanc variant",
      sortable: true,
      filterType: "none",
    },
    {
      key: "created_at",
      name: "DATE CREATED",
      sortable: true,
      filterType: "none",
    },
    {
      key: "updated_at",
      name: "DATE MODIFIED",
      sortable: true,
      filterType: "none",
    },
  ];

  const formatCellValue = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "-";
    }
    if (value == null || value === "") return "-";

    const stringValue = String(value);
    return stringValue.length > 200 ? truncateWithTooltip(stringValue) : stringValue;
  };

  const resolveNestedValue = (obj, key) =>
    key.split(".").reduce((acc, field) => (acc && acc[field] ? acc[field] : null), obj);

  const getSpecialCellValue = (item, key) => {
    if (key === "updated_at" || key === "created_at") return formatDate(item[key]);

    return null;
  };

  // Main function for getting cell value
  const getCellValue = (item, key) => {
    const specialValue = getSpecialCellValue(item, key);
    if (specialValue !== null) return specialValue;

    const value = resolveNestedValue(item, key);
    return formatCellValue(value);
  };

  // Render cell content
  const renderCellContent = (item, col) => {
    const originalValue = getCellValue(item, col.key);

    if (col.renderer) {
      return col.renderer(item, originalValue)
    }

    return <div dangerouslySetInnerHTML={{ __html: originalValue }} />;
  };

  return (
    <div className="page-wrap">
      <div className="header-wrap" ref={headerRef}>
        <div className="header-1" style={{display: 'flex', justifyContent: 'space-between', alignItems:'center'}}>
          <h1>Reusable Objects</h1>
          {showExportControls && <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="button small"
              type="button"
              onClick={() => handleExport(false)}
            >
              Export all
            </button>
            <button
              className="button small"
              type="button"
              disabled={!selectedRows.size}
              onClick={() => handleExport(true)}
            >
              Export selected ({selectedRows.size})
            </button>
          </div>}
        </div>
      </div>
      <div className="table-container" style={{ maxHeight: tableMaxHeight }} ref={tableContainerRef}>
        <table className="table-with-fixed-columns">
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={col.key}
                >
                  {col.key === "select" ? (
                    <input
                      type="checkbox"
                      checked={
                        reusableObjects.length > 0 &&
                        reusableObjects.every((item) => selectedRows.has(item.id))
                      }
                      onChange={handleSelectAll}
                    />
                  ) : (
                  <div className="header-content">
                    <div>
                      <span>{col.name}</span>
                      {col.sortable && (
                        <span
                          className="sort-icon"
                          onClick={() => handleSort(col.key)}
                          style={{cursor:'pointer'}}
                        >
                          {sortColumn === col.key ? (
                            sortDirection === "asc" ? (
                              " 🔼"
                            ) : (
                              " 🔽"
                            )
                          ) : (
                            " ↕️"
                          )}
                        </span>
                      )}
                    </div>
                    <div>
                      {col.filterType === "text" && (
                        <input
                          type="text"
                          placeholder="Filter"
                          defaultValue={filters[col.filterKey || col.key] || ""}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleFilterChange(col.filterKey || col.key, (e.target as HTMLInputElement).value);
                            }
                          }}
                          onBlur={(e) => handleFilterChange(col.filterKey || col.key, (e.target as HTMLInputElement).value)}
                          // onChange={(e) => console.log('changed')}
                        />
                      )}
                      {col.filterType === "multiselect" && (
                        <Select
                          isMulti
                          isClearable
                          isSearchable
                          options={getMultiselectOptions(col.options, true)}
                          defaultValue={getMultiselectOptions(col.options, true).filter(option => filters[col.filterKey || col.key]?.includes(option.value.toString()))}
                          onChange={selected => handleFilterChangeMulti(col.filterKey || col.key, selected as { label: string; value: string; }[])}
                        />
                      )}
                    </div>
                  </div>
                  )}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {reusableObjects.map((lo) => (
              <tr 
                key={lo.id} 
              >
                {columns.map((col, index) => (
                  <td
                    key={col.key}
                  >
                    {renderCellContent(lo, col)}
                  </td>
                ))}
                <td>
                  <VersionsButton itemId={lo.id} itemType="ReusableObject"  itemData={lo} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination-wrap" ref={footerRef}>
        <div className="pagination-per">
          Displaying 
          <select
            value={per}
            onChange={(e) => {
              setPer(parseInt(e.target.value));
              setPage(1);
            }}
          >
            {[10, 20, 50, 100, 500].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          items of {rowCount}
        </div>
        <ReactPaginate
          previousLabel={"Prev"}
          nextLabel={"Next"}
          breakLabel={"..."}
          pageCount={Math.ceil(rowCount / per)}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={(selected) => setPage(selected.selected + 1)}
          containerClassName={"pagination"}
          activeClassName={"active"}
        />
      </div>
        {selectedTeachingPoint && <div className="gen-modal teaching-point-modal">
          <div className="gen-modal-panel">
            <UnwrappedNewEditDocApp
              docType="ReusableObject"
              documentId={selectedTeachingPoint.id}
              versionId={selectedTeachingPoint.publish_variant_id}
              currentUser={props.currentUser}
              permissions={{}}
              defaultChangesDisplayMode={"HIGHLIGHT_CHANGES"}
              displayUid={selectedTeachingPoint.uid}
              isEmbeded
            />
            <button className="actionBtn" onClick={closeTeachingPointModal}>Close</button>
          </div>
        </div>
        }
    </div>
  );
};

export default AdminAssessmentReusableObjects;
